import { getBranchId } from "../utils/branch";
import React, { useState } from "react";
import { Button, Col, Form, Modal, Row, message, Input } from "antd";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { completeTripRequest } from "../redux/actions/completeAction";
import {
  getDistanceRequest,
  resetFareEstimate,
} from "../redux/actions/bookingActions";

const { TextArea } = Input;

interface AssignFeatureProps {
  selectedRowKey: string | null;
  selectedBookingData?: any;
  onCompleteConfirm?: (rowKey: string, values: any) => void;
}

const normalizeBookingId = (booking: any) => {
  return (
    booking?.id ||
    booking?.BookId ||
    booking?.bookid ||
    booking?.bookingId ||
    booking?._id ||
    ""
  );
};

const normalizeCabId = (booking: any) => {
  return (
    booking?.cabId ||
    booking?.cabid ||
    booking?.vehicleid ||
    booking?.cabno ||
    booking?.vehicletype ||
    ""
  );
};

const isValidCoordinate = (value: any) => {
  return value !== null && value !== undefined && value !== "" && !Number.isNaN(Number(value));
};

const parseNumericValue = (value: any) => {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const match = value.match(/([0-9]+(?:\.[0-9]+)?)/);
    return match ? Number(match[1]) : undefined;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
};

const CompleteModel: React.FC<AssignFeatureProps> = ({ selectedRowKey, selectedBookingData, onCompleteConfirm }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const fareEstimate = useAppSelector((state) => state.booking.fareEstimate);
  const bookingDistance = useAppSelector((state) => state.booking.distance);
  const roleid = useAppSelector((state) => state.auth.roleid);
  const canEditFinancials = [7, 10].includes(Number(roleid));
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const formatTimeValue = (value: any) => {
    if (value === undefined || value === null || value === "") return undefined;
    const numeric = parseNumericValue(value);
    if (numeric !== undefined) return `${numeric} mins`;
    if (typeof value === "string") return value;
    return String(value);
  };

  const getFareTypeId = (fareType: any) => {
    if (!fareType) return 1;
    const normalized = String(fareType).toLowerCase();
    if (normalized.includes("meter")) return 1;
    if (normalized.includes("package")) return 2;
    if (normalized.includes("intercity")) return 3;
    if (normalized.includes("night")) return 4;
    return 1;
  };

  const handleAssignClick = () => {
    if (!selectedRowKey) {
      message.warning("Please select a row first");
      return;
    }
    setOpen(true);
  };

  React.useEffect(() => {
    if (open && selectedBookingData) {
      const fromlat = selectedBookingData?.fromloclat;
      const fromlong = selectedBookingData?.fromloclong;
      const tolat = selectedBookingData?.toloclat;
      const tolong = selectedBookingData?.toloclong;
      const vehicleId = selectedBookingData?.vehicleid || selectedBookingData?.cabid || selectedBookingData?.cabno || selectedBookingData?.vehicletypeid;
      const branchid = selectedBookingData?.branchid || getBranchId();
      const faretypeId = getFareTypeId(selectedBookingData?.faretype);

      if (
        isValidCoordinate(fromlat) &&
        isValidCoordinate(fromlong) &&
        isValidCoordinate(tolat) &&
        isValidCoordinate(tolong) &&
        vehicleId
      ) {
        dispatch(resetFareEstimate());
        dispatch(
          getDistanceRequest(
            String(fromlat),
            String(fromlong),
            String(tolat),
            String(tolong),
            branchid,
            String(vehicleId),
            faretypeId
          )
        );
      }

      form.setFieldsValue({
        bookingId: normalizeBookingId(selectedBookingData),
        cabId: normalizeCabId(selectedBookingData),
        location: selectedBookingData.toloc || selectedBookingData.fromloc,
        km: (() => {
          const val = selectedBookingData?.km ?? selectedBookingData?.distance;
          if (val === undefined || val === null || val === "") return undefined;
          if (typeof val === "string") {
            const m = val.match(/([0-9]+(?:\.[0-9]+)?)/);
            return m ? m[1] : val;
          }
          return val;
        })(),
      });
    }
  }, [dispatch, form, open, selectedBookingData]);

  React.useEffect(() => {
    if (!open || !selectedBookingData) return;

    const timeField = form.getFieldValue("time");
    const fareField = form.getFieldValue("fare");

    const valuesToSet: any = {};

    const distanceFromResponse = parseNumericValue(
      bookingDistance?.data?.[0]?.distance ?? bookingDistance?.distance
    );
    if (distanceFromResponse !== undefined) {
      valuesToSet.km = distanceFromResponse;
    }

    if (!fareField) {
      valuesToSet.fare = fareEstimate?.approximatefare ?? selectedBookingData?.fare;
    }

    if (!timeField) {
      const durationFromResponse = bookingDistance?.data?.[0]?.duration ?? bookingDistance?.duration;
      const fallbackTime =
        durationFromResponse ??
        fareEstimate?.baseminute ??
        selectedBookingData?.tripminutes ??
        selectedBookingData?.actualMinutes ??
        selectedBookingData?.minutes;

      const normalizedTime = formatTimeValue(fallbackTime || selectedBookingData?.time || selectedBookingData?.triptime);
      if (normalizedTime) {
        valuesToSet.time = normalizedTime;
      }
    }

    if (Object.keys(valuesToSet).length) {
      form.setFieldsValue(valuesToSet);
    }
  }, [bookingDistance, fareEstimate, form, open, selectedBookingData]);

  const handleComplete = () => {
    form.validateFields().then((values) => {
      const bookingId = normalizeBookingId(selectedBookingData);
      const cabId = normalizeCabId(selectedBookingData);

      if (!bookingId || !cabId) {
        message.warning("Selected booking does not contain a valid booking or cab ID.");
        return;
      }

      const piclat = isValidCoordinate(selectedBookingData?.toloclat)
        ? selectedBookingData.toloclat
        : isValidCoordinate(selectedBookingData?.fromloclat)
          ? selectedBookingData.fromloclat
          : null;
      const piclon = isValidCoordinate(selectedBookingData?.toloclong)
        ? selectedBookingData.toloclong
        : isValidCoordinate(selectedBookingData?.fromloclong)
          ? selectedBookingData.fromloclong
          : null;

      const payload = {
        bookid: bookingId,
        bookingid: bookingId,
        bookingId,
        cabid: cabId,
        cabId,
        distance: parseNumericValue(values.km),
        mins: parseNumericValue(values.time),
        fare: parseNumericValue(values.fare) ?? Number(values.fare) ?? 0,
        location: values.location,
        feedback: values.feedback || "",
        completedby: user?.username || "",
        iscredit: selectedBookingData?.iscredit,
        piclat,
        piclon,
        present_location: values.location || selectedBookingData?.toloc || selectedBookingData?.fromloc || "",
      };

      if (onCompleteConfirm && selectedRowKey) {
        onCompleteConfirm(selectedRowKey, payload);
      } else {
        dispatch(completeTripRequest(payload));
      }

      setOpen(false);
      form.resetFields();
    }).catch((info) => {
      //console.log('Validate Failed:', info);
    });
  };

  return (
    <>
      <Button className="action-btn2 complete" onClick={handleAssignClick} disabled={!selectedRowKey}>
        Complete
      </Button>

      <Modal
        title="Complete Trip"
        open={open}
        centered
        closable={false}
        width={{ xs: "94vw", md: 750 }}
        footer={
          <Row justify="center" style={{ gap: "16px", marginTop: "24px" }}>
            <Button
              type="default"
              size="large"
              style={{ minWidth: 140, borderRadius: 8, fontWeight: 500 }}
              onClick={() => {
                setOpen(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleComplete}
              style={{
                minWidth: 140,
                borderRadius: 8,
                background: "linear-gradient(135deg, #89339c, #b56ac8)",
                border: "none",
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(137, 67, 154, 0.3)"
              }}
            >
              Complete Trip
            </Button>
          </Row>
        }
        styles={{
          header: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            background: "linear-gradient(135deg, #89339C, #A74EBB)",
            color: "#ffffff",
            borderBottom: "none",
            padding: "20px 24px",
            textAlign: "center",
          },
          title: {
            color: "#ffffff",
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "0.5px",
            margin: 0,
          },
          body: { paddingTop: 24, paddingBottom: 0, paddingLeft: 32, paddingRight: 32 },
          //content: { borderRadius: 16, padding: 0 }
        }}
        modalRender={(modal) => (
          <div style={{ borderRadius: 16, overflow: "hidden" }}>{modal}</div>
        )}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, color: "#444" }}>Booking ID</span>}
                name="bookingId"
                rules={[{ required: true, message: "Enter booking ID" }]}
              >
                <Input size="large" placeholder="Enter booking id" style={{ borderRadius: 8, height: 46 }} readOnly />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, color: "#444" }}>Cab ID</span>}
                name="cabId"
                rules={[{ required: true, message: "Enter Cab ID" }]}
              >
                <Input size="large" placeholder="Enter Cab id" style={{ borderRadius: 8, height: 46 }} readOnly />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, color: "#444" }}>Km Travelled</span>}
                name="km"
                rules={[{ required: true, message: "Enter Km travelled" }]}
              >
                <Input
                  size="large"
                  placeholder="E.g., 25.5"
                  suffix="km"
                  style={{ borderRadius: 8, height: 46 }}
                  disabled={!canEditFinancials}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, color: "#444" }}>Trip Time</span>}
                name="time"
                rules={[{ required: true, message: "Enter trip time" }]}
              >
                <Input
                  size="large"
                  placeholder="E.g., 45 mins"
                  style={{ borderRadius: 8, height: 46 }}
                  disabled={!canEditFinancials}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, color: "#444" }}>Fare Amount</span>}
                name="fare"
                rules={[{ required: true, message: "Enter fare amount" }]}
              >
                <Input
                  size="large"
                  prefix="₹"
                  placeholder="0.00"
                  style={{ borderRadius: 8, height: 46 }}
                  disabled={!canEditFinancials}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<span style={{ fontWeight: 600, color: "#444" }}>End Location</span>}
                name="location"
                rules={[{ required: true, message: "Enter end location" }]}
              >
                <Input size="large" placeholder="Enter drop location" style={{ borderRadius: 8, height: 46 }} readOnly />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={<span style={{ fontWeight: 600, color: "#444" }}>Feedback or Remarks</span>}
                name="feedback"
              >
                <TextArea size="large" rows={3} placeholder="Any feedback for the trip..." style={{ borderRadius: 8, resize: "none" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CompleteModel;

